import { useCallback, useRef, useState } from 'react';
import {
  MeasuringStrategy, MouseSensor, TouchSensor, getClientRect, pointerWithin, useSensor, useSensors,
} from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import { MAX_PLAYER_COUNT, getWorld, clampToRect } from '../utils/boardGeometry';

/**
 * dnd-kit-Verdrahtung des Playtest-Bretts: Sensoren, Kollision, Drop-Auflösung → Socket-Aktionen.
 *
 * Jede ziehbare Karte meldet sich mit
 *   useDraggable({ id: dragId(card.instanceId), data: dragData(card, fromZone) })
 * an; ohne `fromZone` in den Daten löst ein Drop nichts aus. Ablageziele heißen wie `DROP_IDS`.
 *
 * Drop-Auflösung (`onDragEnd`):
 *   - kein Ziel (Loslassen über einer Leiste) → nichts, keine Vormerkung
 *   - Brettfläche aus der Hand → `playCard`, aus Friedhof/Exil/Commander-Zone/Bibliothek →
 *     `moveCardZone` mit Position, von der Brettfläche → `updateCardPosition` (nur bei Bewegung)
 *   - Zone → `moveCardZone` (Bibliothek oben/unten über `insertAtTop`, auch aus der Bibliothek
 *     selbst); sonst dieselbe Zone → nichts
 * Die Position ist die Kartenmitte in Weltkoordinaten: Zeiger minus Griff-Abstand zur Mitte der
 * gezogenen Karte, umgerechnet über `toWorld` (Client-Koordinaten → Welt) und in die Welt
 * geklemmt (`getWorld(playerCount)`: die Welt der aktuellen Teilnehmerzahl, nie darüber hinaus;
 * fehlt `playerCount`, gilt die größte Welt — lieber zu weit als eine Karte an den Rand einer zu
 * kleinen Welt zu klemmen); liefert `toWorld` noch nichts (Kamera ohne Viewport), fällt der
 * Drop aus. Kommt die Karte nicht von der Brettfläche, wird der Griff-Abstand auf die Brettgröße
 * (`cardSizePx`) skaliert, damit der Zeiger auf derselben Stelle der Karte bleibt.
 * Griff und Maßstab misst der Zugstart an der Quelle selbst (das Element mit
 * `aria-roledescription="draggable"` unter dem Zeiger), nicht an `active.rect`: sobald das
 * Overlay steht, misst dnd-kit dort die Overlay-Karte in Brettgröße, und der Maßstab wäre 1 —
 * eine außerhalb der Mitte gegriffene Zonen- oder Handkarte landete dann neben dem Finger.
 * Nur ohne messbare Quelle gilt `active.rect` beim ersten Bewegungs-Ereignis.
 *
 * Langdruck (Handy): ein per Finger gestarteter Zug, der nie weiter als `LONG_PRESS_TOLERANCE_PX`
 * bewegt wurde und beim Loslassen `LONG_PRESS_MS` ab dem Aufsetzen des Fingers gehalten wurde, ist
 * keine Ablage, sondern ein Langdruck → `onLongPress(instanceId, fromZone)`. Der Touch-Sensor
 * startet den Zug erst nach `TOUCH_ACTIVATION_DELAY_MS` Stillhalten; gemessen wird ab dem Start
 * des Zugs, also mit dieser Verzögerung abgezogen. Ein eigener Langdruck-Zähler auf der Karte käme
 * nie zum Zug, weil der Touch-Sensor den Finger vorher übernimmt.
 *
 * Kollision: der Zeiger entscheidet (`pointerWithin`), nicht die Fläche der gezogenen Karte;
 * liegt er über einer Zone und der Brettfläche zugleich, gewinnt die Zone.
 *
 * Messen der Ablageziele (`measuring`, siehe `DROP_MEASURING`): mit Transform, also dort, wo das
 * Ziel tatsächlich zu sehen ist, und während eines Zugs alle `DROP_REMEASURE_MS` nach einer
 * Bewegung neu.
 *
 * Rückgabe für `<DndContext sensors collisionDetection measuring onDragStart onDragMove onDragEnd
 * onDragCancel>` und `<DragOverlay>`: `activeCard` ist die gezogene Karte, `overlayOffset` der
 * Pixel-Abstand von der Mitte der Overlay-Hülle (Größe der Quelle) zur Mitte der Brettkarte
 * (siehe `DragOverlayCard`).
 */
export const DROP_IDS = {
  battlefield: 'battlefield',
  graveyard: 'zone:graveyard',
  exile: 'zone:exile',
  deckTop: 'zone:deck-top',
  deckBottom: 'zone:deck-bottom',
  hand: 'zone:hand',
  commandZone: 'zone:commandZone',
};

/** Aktivierung der Maus erst ab dieser Strecke; Klicks mit kürzerem Weg bleiben Klicks. */
export const DRAG_DISTANCE_PX = 5;

/** Mindestdauer eines Langdrucks am Handy, ab dem Aufsetzen des Fingers. */
export const LONG_PRESS_MS = 600;

/** So lange muss der Finger ruhen, bis der Touch-Sensor einen Zug startet. */
export const TOUCH_ACTIVATION_DELAY_MS = 150;

/** Weg, den ein ruhender Finger beim Halten zittern darf, ohne dass der Langdruck zum Zug wird. */
export const LONG_PRESS_TOLERANCE_PX = 10;

/** Abstand, in dem die Ablageziele während eines Zugs nach einer Bewegung neu gemessen werden. */
export const DROP_REMEASURE_MS = 100;

/**
 * Ablageziele mit ihrem eigenen Transform messen: dnd-kit rechnet ihn sonst heraus, und der in Ruhe
 * per translateY halb aus dem Bild geschobene Handfächer zählte mit seiner vollen, unverschobenen
 * Fläche — ein Streifen über der sichtbaren Hand schickte Karten dorthin zurück statt aufs Brett.
 * Neu gemessen wird während des Zugs, weil sich der Fächer dabei hebt und senkt (Rechner: Zeiger
 * darüber; Handy: Senken beim Zugstart).
 */
export const DROP_MEASURING = {
  droppable: { measure: getClientRect, strategy: MeasuringStrategy.WhileDragging, frequency: DROP_REMEASURE_MS },
};

const DRAG_PREFIX = 'card:';

const ZONE_TARGETS = {
  [DROP_IDS.graveyard]: { toZone: 'graveyard' },
  [DROP_IDS.exile]: { toZone: 'exile' },
  [DROP_IDS.hand]: { toZone: 'hand' },
  [DROP_IDS.commandZone]: { toZone: 'commandZone' },
  [DROP_IDS.deckTop]: { toZone: 'deck', insertAtTop: true },
  [DROP_IDS.deckBottom]: { toZone: 'deck', insertAtTop: false },
};

export function dragId(instanceId) {
  return `${DRAG_PREFIX}${instanceId}`;
}

export function parseDragId(id) {
  if (typeof id !== 'string' || !id.startsWith(DRAG_PREFIX)) return null;
  return id.slice(DRAG_PREFIX.length) || null;
}

export function dragData(card, fromZone) {
  return { card, fromZone };
}

function isTouchEvent(event) {
  return typeof event?.type === 'string' && event.type.startsWith('touch');
}

function preferZones(args) {
  const hits = pointerWithin(args);
  const zone = hits.find((hit) => hit.id !== DROP_IDS.battlefield);
  return zone ? [zone] : hits;
}

/** Rechteck der Zieh-Quelle unter dem Zeiger, wie sie gerade zu sehen ist; null ohne DOM-Ziel. */
function sourceRect(activatorEvent) {
  const node = activatorEvent?.target?.closest?.('[aria-roledescription="draggable"]');
  return node ? node.getBoundingClientRect() : null;
}

/** Griff-Abstand (Zeiger minus Kartenmitte am Start) und Skalierung auf die Brettgröße. */
function measureGrab(rect, active, activatorEvent, cardSizePx) {
  const start = activatorEvent ? getEventCoordinates(activatorEvent) : null;
  if (!rect || !start) return null;
  const fromZone = active?.data?.current?.fromZone;
  const scale = fromZone === 'battlefield' || !(rect.width > 0) || !(cardSizePx > 0)
    ? 1
    : cardSizePx / rect.width;
  return {
    start,
    grab: { x: start.x - (rect.left + rect.width / 2), y: start.y - (rect.top + rect.height / 2) },
    scale,
  };
}

export function usePlaytestDnd({ actions, toWorld, cardSizePx, playerCount, onLongPress }) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: DRAG_DISTANCE_PX } }),
    useSensor(TouchSensor, { activationConstraint: { delay: TOUCH_ACTIVATION_DELAY_MS, tolerance: 5 } }),
  );
  const [activeCard, setActiveCard] = useState(null);
  const [activeIsTouch, setActiveIsTouch] = useState(false);
  const [overlayOffset, setOverlayOffset] = useState({ x: 0, y: 0 });
  // Griff des laufenden Zugs (`measureGrab`), gemessen beim Start bzw. ersten Bewegungs-Ereignis.
  const grabRef = useRef(null);

  const pressRef = useRef(null);

  const latest = useRef({ actions, toWorld, cardSizePx, playerCount, onLongPress });
  latest.current = { actions, toWorld, cardSizePx, playerCount, onLongPress };

  const reset = useCallback(() => {
    setActiveCard(null);
    setActiveIsTouch(false);
    setOverlayOffset({ x: 0, y: 0 });
    grabRef.current = null;
  }, []);

  const applyGrab = useCallback((measured) => {
    grabRef.current = measured;
    const { grab, scale } = measured;
    setOverlayOffset({ x: grab.x * (1 - scale), y: grab.y * (1 - scale) });
  }, []);

  const onDragStart = useCallback(({ active, activatorEvent }) => {
    pressRef.current = { touch: isTouchEvent(activatorEvent), startedAt: Date.now(), moved: false };
    grabRef.current = null;
    setOverlayOffset({ x: 0, y: 0 });
    setActiveCard(active?.data?.current?.card ?? null);
    setActiveIsTouch(pressRef.current.touch);
    const measured = measureGrab(sourceRect(activatorEvent), active, activatorEvent, latest.current.cardSizePx);
    if (measured) applyGrab(measured);
  }, [applyGrab]);

  const onDragMove = useCallback(({ active, activatorEvent, delta }) => {
    const press = pressRef.current;
    if (press && delta && Math.hypot(delta.x, delta.y) > LONG_PRESS_TOLERANCE_PX) press.moved = true;
    if (grabRef.current) return;
    const measured = measureGrab(active?.rect?.current?.initial, active, activatorEvent, latest.current.cardSizePx);
    if (measured) applyGrab(measured);
  }, [applyGrab]);

  const onDragEnd = useCallback(({ active, over, delta, activatorEvent }) => {
    const press = pressRef.current;
    const startGrab = grabRef.current;
    pressRef.current = null;
    reset();
    const instanceId = parseDragId(active?.id);
    const fromZone = active?.data?.current?.fromZone;
    if (!instanceId || !fromZone) return;
    const { actions: act, toWorld: convert, cardSizePx: size, playerCount: players, onLongPress: longPress } = latest.current;

    const still = !delta || Math.hypot(delta.x, delta.y) <= LONG_PRESS_TOLERANCE_PX;
    if (press?.touch && !press.moved && still && Date.now() - press.startedAt >= LONG_PRESS_MS - TOUCH_ACTIVATION_DELAY_MS) {
      longPress?.(instanceId, fromZone);
      return;
    }
    if (!over) return;

    if (over.id === DROP_IDS.battlefield) {
      if (fromZone === 'battlefield' && Math.hypot(delta.x, delta.y) < DRAG_DISTANCE_PX) return;
      const measured = startGrab ?? measureGrab(active?.rect?.current?.initial, active, activatorEvent, size);
      if (!measured) return;
      const { start, grab, scale } = measured;
      const center = {
        x: start.x + delta.x - grab.x * scale,
        y: start.y + delta.y - grab.y * scale,
      };
      const world = convert(center);
      if (!world) return;
      const bounds = getWorld(players ?? MAX_PLAYER_COUNT);
      const position = clampToRect(world, {
        x: bounds.minX, y: bounds.minY, w: bounds.maxX - bounds.minX, h: bounds.maxY - bounds.minY,
      });
      if (fromZone === 'battlefield') act.updateCardPosition(instanceId, position);
      else if (fromZone === 'hand') act.playCard(instanceId, position);
      else act.moveCardZone({ instanceId, fromZone, toZone: 'battlefield', position });
      return;
    }

    const target = ZONE_TARGETS[over.id];
    // Bibliothek → Bibliothek oben/unten ist eine echte Umsortierung, keine Ablage am Herkunftsort.
    if (!target || (target.toZone === fromZone && fromZone !== 'deck')) return;
    act.moveCardZone({ instanceId, fromZone, ...target });
  }, [reset]);

  const onDragCancel = useCallback(() => {
    pressRef.current = null;
    reset();
  }, [reset]);

  return {
    sensors,
    measuring: DROP_MEASURING,
    collisionDetection: preferZones,
    onDragStart,
    onDragMove,
    onDragEnd,
    onDragCancel,
    activeCard,
    activeIsTouch,
    overlayOffset,
  };
}
